import { type ReactNode } from 'react';
import type { FlowNodeData, FlowEdgeData, FlowOrientation } from './flow.types.js';
import { outputFraction } from './FlowNode.js';

export interface FlowEdgeProps {
  edge: FlowEdgeData;
  source: FlowNodeData;
  target: FlowNodeData;
  /** Color override (defaults to the canvas's edge token). */
  color?: string;
  selected?: boolean;
  /** Dim the edge (another selection has the focus). */
  muted?: boolean;
  onClick?: (edge: FlowEdgeData) => void;
  /** With this, a selected edge shows a delete button at its midpoint. */
  onDelete?: (edge: FlowEdgeData) => void;
  deleteLabel?: string;
  /**
   * With this, every edge carries a small "+" so a step can be put on the
   * link itself. The anchor is the button's client position, for a menu.
   */
  onInsert?: (edge: FlowEdgeData, anchor: { clientX: number; clientY: number }) => void;
  insertLabel?: string;
  orientation?: FlowOrientation;
}

/** The point an edge leaves a node from, honouring the node's output ports. */
export function sourcePoint(source: FlowNodeData, handle: string | undefined, orientation: FlowOrientation): { x: number; y: number } {
  const w = source.width ?? 200;
  const h = source.height ?? 80;
  const outputs = source.outputs ?? [];
  const index = handle ? outputs.findIndex((o) => o.id === handle) : -1;
  const f = outputs.length === 0 ? 0.5 : outputFraction(index < 0 ? 0 : index, outputs.length);
  return orientation === 'vertical'
    ? { x: source.position.x + w * f, y: source.position.y + h }
    : { x: source.position.x + w, y: source.position.y + h * f };
}

/** The point an edge enters a node at. */
export function targetPoint(target: FlowNodeData, orientation: FlowOrientation): { x: number; y: number } {
  const w = target.width ?? 200;
  const h = target.height ?? 80;
  return orientation === 'vertical'
    ? { x: target.position.x + w / 2, y: target.position.y }
    : { x: target.position.x, y: target.position.y + h / 2 };
}

/**
 * A cubic Bézier from `s` to `t` that leaves and arrives along the reading
 * direction. When the target sits behind the source (an edge drawn upwards
 * or leftwards) the control points swing out so the curve stays readable
 * instead of collapsing into a line through both nodes.
 */
export function edgePath(s: { x: number; y: number }, t: { x: number; y: number }, orientation: FlowOrientation): string {
  if (orientation === 'vertical') {
    const dy = Math.max(48, Math.abs(t.y - s.y) / 2);
    return `M ${s.x},${s.y} C ${s.x},${s.y + dy} ${t.x},${t.y - dy} ${t.x},${t.y}`;
  }
  const dx = Math.max(48, Math.abs(t.x - s.x) / 2);
  return `M ${s.x},${s.y} C ${s.x + dx},${s.y} ${t.x - dx},${t.y} ${t.x},${t.y}`;
}

/** A point on the cubic at parameter `u` (0..1), for the label and the delete button. */
export function edgeMidpoint(s: { x: number; y: number }, t: { x: number; y: number }, orientation: FlowOrientation, u = 0.5): { x: number; y: number } {
  const vertical = orientation === 'vertical';
  const d = vertical ? Math.max(48, Math.abs(t.y - s.y) / 2) : Math.max(48, Math.abs(t.x - s.x) / 2);
  const c1 = vertical ? { x: s.x, y: s.y + d } : { x: s.x + d, y: s.y };
  const c2 = vertical ? { x: t.x, y: t.y - d } : { x: t.x - d, y: t.y };
  const k = 1 - u;
  return {
    x: k * k * k * s.x + 3 * k * k * u * c1.x + 3 * k * u * u * c2.x + u * u * u * t.x,
    y: k * k * k * s.y + 3 * k * k * u * c1.y + 3 * k * u * u * c2.y + u * u * u * t.y
  };
}

/**
 * SVG edge with a cubic Bézier curve and an arrowhead. Source/target points
 * are computed from each node's `position` + `width`/`height` (defaults
 * 200×80), and from the output port the edge leaves when the node has several.
 * A wide transparent twin of the path makes the edge easy to click.
 */
export function FlowEdge({
  edge, source, target, color, selected, muted, onClick, onDelete, deleteLabel = 'Remove link', onInsert, insertLabel = 'Insert a step here', orientation = 'horizontal'
}: FlowEdgeProps): ReactNode {
  const s = sourcePoint(source, edge.sourceHandle, orientation);
  const t = targetPoint(target, orientation);
  const d = edgePath(s, t, orientation);
  const mid = edgeMidpoint(s, t, orientation);
  // With a "+" on the link as well, the pill moves up and the "+" down so both read.
  const labelAt = onInsert && edge.label ? edgeMidpoint(s, t, orientation, 0.36) : mid;
  const plus = edge.label ? edgeMidpoint(s, t, orientation, 0.76) : mid;
  const tone = typeof edge.data?.tone === 'string' ? edge.data.tone : undefined;
  const stroke = selected ? 'var(--rr-color-primary)' : (color ?? 'var(--rr-flow-edge, #94a3b8)');
  const labelWidth = edge.label ? edge.label.length * 6.6 + 16 : 0;

  return (
    <g
      className={[
        'rr-flow-edge',
        selected && 'rr-flow-edge--selected',
        muted && 'rr-flow-edge--muted'
      ].filter(Boolean).join(' ')}
      data-edge-id={edge.id}
      data-tone={tone}
      style={{ cursor: onClick ? 'pointer' : 'default' }}
      onClick={(e) => { e.stopPropagation(); onClick?.(edge); }}
    >
      {/* The hit area: fat and invisible, so a 1.5px line is not a 1.5px target. */}
      <path d={d} stroke="transparent" strokeWidth={14} fill="none" />
      <path
        className="rr-flow-edge__line"
        d={d}
        stroke={stroke}
        strokeWidth={selected ? 2.5 : 2}
        fill="none"
        strokeDasharray={edge.dashed ? '6 5' : undefined}
        markerEnd={`url(#rr-flow-arrow${selected ? '-active' : ''})`}
      />
      {edge.label ? (
        // A pill on the midpoint, so "Yes" and "No" read over the grid.
        <g className="rr-flow-edge__label" transform={`translate(${labelAt.x}, ${labelAt.y})`}>
          <rect
            x={-labelWidth / 2}
            y={-10}
            width={labelWidth}
            height={20}
            rx={10}
            fill="var(--rr-flow-edge-label-bg, #ffffff)"
            stroke={stroke}
            strokeWidth={1}
          />
          <text
            fontSize="11"
            fontWeight={600}
            fontFamily="var(--rr-font-system)"
            fill={selected ? 'var(--rr-color-primary)' : 'var(--rr-flow-edge-label, #475569)'}
            textAnchor="middle"
            dominantBaseline="central"
          >
            {edge.label}
          </text>
        </g>
      ) : null}
      {onInsert ? (
        // Put a step on this link.
        <g
          className="rr-flow-edge__insert"
          transform={`translate(${plus.x}, ${plus.y})`}
          role="button"
          aria-label={insertLabel}
          onClick={(e) => { e.stopPropagation(); onInsert(edge, { clientX: e.clientX, clientY: e.clientY }); }}
        >
          <title>{insertLabel}</title>
          <circle r={10} fill="var(--rr-flow-edge-button-bg, #ffffff)" stroke={stroke} strokeWidth={1.2} />
          <path d="M -4 0 L 4 0 M 0 -4 L 0 4" stroke={stroke} strokeWidth={1.8} strokeLinecap="round" />
        </g>
      ) : null}
      {onDelete ? (
        // Remove the link: beside the "+" (or on the midpoint), shown when the link is hovered or selected.
        <g
          className="rr-flow-edge__delete"
          transform={`translate(${onInsert ? (orientation === 'vertical' ? plus.x + 26 : plus.x) : mid.x + (edge.label ? labelWidth / 2 + 16 : 0)}, ${onInsert ? (orientation === 'vertical' ? plus.y : plus.y + 26) : mid.y})`}
          role="button"
          aria-label={deleteLabel}
          onClick={(e) => { e.stopPropagation(); onDelete(edge); }}
        >
          <title>{deleteLabel}</title>
          <circle r={11} fill="var(--rr-flow-edge-button-bg, #ffffff)" stroke="var(--rr-color-error, #dc2626)" strokeWidth={1.2} />
          <path d="M -4 -4 L 4 4 M 4 -4 L -4 4" stroke="var(--rr-color-error, #dc2626)" strokeWidth={1.8} strokeLinecap="round" />
        </g>
      ) : null}
    </g>
  );
}

/** Reusable arrow markers; render once inside the canvas svg's `<defs>`. */
export function FlowEdgeMarkers(): ReactNode {
  return (
    <defs>
      <marker id="rr-flow-arrow" viewBox="0 0 10 10" refX="9" refY="5" markerWidth="7" markerHeight="7" orient="auto-start-reverse">
        <path d="M 0 0 L 10 5 L 0 10 z" fill="var(--rr-flow-edge, #94a3b8)" />
      </marker>
      <marker id="rr-flow-arrow-active" viewBox="0 0 10 10" refX="9" refY="5" markerWidth="7" markerHeight="7" orient="auto-start-reverse">
        <path d="M 0 0 L 10 5 L 0 10 z" fill="var(--rr-color-primary)" />
      </marker>
    </defs>
  );
}
